// Gráfico de barras multi-série (SVG inline, sem dependências). Port literal de buildBarChartEl
// (index.html:3401). O gráfico de linhas do protótipo (buildMultiLineChartEl) nunca era chamado.

import { esc } from './util.js';

export const CHART_PALETTE = ['#1F3864', '#2563eb', '#16a34a', '#d97706', '#dc2626', '#9333ea', '#0d9488', '#be185d', '#65a30d', '#0891b2', '#ea580c', '#4338ca'];
let _chartIdSeq = 0;

export function buildBarChartEl(labels: string[], series: { name: string; color: string; values: (number | null)[] }[], colorFn?: (v: number) => string, opts?: any): HTMLElement {
  let firstIdx: number | null = null, lastIdx: number | null = null;
  labels.forEach(function (_, i) {
    const any = series.some(function (s) { return s.values[i] !== null && s.values[i] !== undefined; });
    if (any) { if (firstIdx === null) firstIdx = i; lastIdx = i; }
  });
  if (firstIdx === null) { firstIdx = 0; lastIdx = labels.length - 1; }
  const trimLabels = labels.slice(firstIdx, (lastIdx as number) + 1);
  const trimSeries = series.map(function (s) { return { name: s.name, color: s.color, values: s.values.slice(firstIdx as number, (lastIdx as number) + 1) }; });

  function lighten(hex: string, amt: number) {
    let h = hex.replace('#', '');
    if (h.length === 3) h = h[0]! + h[0] + h[1] + h[1] + h[2] + h[2];
    let r = parseInt(h.substring(0, 2), 16), g = parseInt(h.substring(2, 4), 16), b = parseInt(h.substring(4, 6), 16);
    r = Math.round(r + (255 - r) * amt); g = Math.round(g + (255 - g) * amt); b = Math.round(b + (255 - b) * amt);
    return 'rgb(' + r + ',' + g + ',' + b + ')';
  }

  const W = (opts && opts.W) || 680, H = (opts && opts.H) || 230, padL = 34, padR = 14, padT = 28, padB = 28;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const n = trimLabels.length;
  const nSeries = trimSeries.length;
  const groupW = n > 0 ? plotW / n : 0;
  const maxBarW = nSeries > 1 ? 32 : 30;
  const barGap = 4;
  const rawBarW = (groupW - (nSeries - 1) * barGap) / nSeries * 0.62;
  const barW = Math.max(4, Math.min(maxBarW, rawBarW));
  const groupPad = (groupW - (barW * nSeries + barGap * (nSeries - 1))) / 2;
  function yAt(v: number) { return padT + plotH - (v / 100 * plotH); }
  function groupX(i: number) { return padL + groupW * i; }
  function barX(i: number, si: number) { return groupX(i) + groupPad + si * (barW + barGap); }
  const baseY = yAt(0);
  const chartId = 'mbc' + (_chartIdSeq++);

  let grid = '';
  [0, 50, 100].forEach(function (g) {
    const y = yAt(g);
    grid += '<line x1="' + padL + '" y1="' + y + '" x2="' + (W - padR) + '" y2="' + y + '" stroke="#f1f5f9" stroke-width="1"/>';
    grid += '<text x="' + (padL - 8) + '" y="' + (y + 4) + '" font-size="10.5" fill="#94a3b8" text-anchor="end">' + g + '</text>';
  });

  let gradDefs = ''; const gradIds: Record<string, string> = {};
  function gradFor(color: string) {
    if (gradIds[color]) return gradIds[color];
    const id = chartId + '-g' + Object.keys(gradIds).length;
    gradIds[color] = id;
    gradDefs += '<linearGradient id="' + id + '" x1="0" y1="0" x2="0" y2="1">'
      + '<stop offset="0%" stop-color="' + lighten(color, .3) + '"/>'
      + '<stop offset="100%" stop-color="' + color + '"/>'
      + '</linearGradient>';
    return id;
  }

  let groupBgSvg = '', barsSvg = '', labelsSvg = '';
  trimLabels.forEach(function (_l, i) {
    groupBgSvg += '<rect class="' + chartId + '-groupbg" data-i="' + i + '" x="' + groupX(i).toFixed(1) + '" y="' + padT + '" width="' + groupW.toFixed(1) + '" height="' + plotH + '" rx="4" fill="#1F3864" opacity="0" style="transition:opacity .15s"/>';
  });
  trimSeries.forEach(function (s, si) {
    s.values.forEach(function (v, i) {
      if (v === null || v === undefined) return;
      const x = barX(i, si), finalY = yAt(v), h = baseY - finalY;
      const color = colorFn ? colorFn(v) : s.color;
      const gid = gradFor(color);
      barsSvg += '<rect class="' + chartId + '-bar" data-si="' + si + '" data-i="' + i + '" x="' + x.toFixed(1) + '" y="' + baseY.toFixed(1) + '" width="' + barW.toFixed(1) + '" height="0" rx="' + (barW / 2.6).toFixed(1) + '" fill="url(#' + gid + ')" data-final-y="' + finalY.toFixed(1) + '" data-final-h="' + h.toFixed(1) + '" style="transition:opacity .2s"/>';
      labelsSvg += '<text class="' + chartId + '-label" data-si="' + si + '" data-i="' + i + '" x="' + (x + barW / 2).toFixed(1) + '" y="' + (finalY - 8).toFixed(1) + '" font-size="11" font-weight="800" fill="' + color + '" text-anchor="middle" opacity="0" style="transition:opacity .3s">' + v.toFixed(0) + '%</text>';
    });
  });

  let xlabels = '';
  trimLabels.forEach(function (l, i) {
    xlabels += '<text x="' + (groupX(i) + groupW / 2).toFixed(1) + '" y="' + (H - 8) + '" font-size="10.5" fill="#64748b" text-anchor="middle">' + esc(l) + '</text>';
  });

  const svgStr = '<svg class="' + chartId + '-svg" viewBox="0 0 ' + W + ' ' + H + '" style="width:100%;max-width:' + ((opts && opts.full) ? '100%' : '600px') + ';height:auto;display:block" preserveAspectRatio="xMidYMid meet">'
    + '<defs>' + gradDefs + '</defs>'
    + grid
    + '<g class="' + chartId + '-groupbgs">' + groupBgSvg + '</g>'
    + '<g class="' + chartId + '-bars">' + barsSvg + '</g>'
    + '<g class="' + chartId + '-labels">' + labelsSvg + '</g>'
    + xlabels
    + '<rect class="' + chartId + '-overlay" x="' + padL + '" y="' + padT + '" width="' + plotW + '" height="' + plotH + '" fill="transparent" style="cursor:pointer"/>'
    + '</svg>';

  const wrap = document.createElement('div');
  wrap.style.cssText = 'position:relative';
  wrap.innerHTML = svgStr;

  if (trimSeries.length > 1) {
    const legend = document.createElement('div');
    legend.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px 10px;margin-top:14px;padding-top:12px;border-top:1px solid #f1f5f9';
    trimSeries.forEach(function (s, si) {
      let lastVal: number | null = null;
      for (let i = s.values.length - 1; i >= 0; i--) { const vv = s.values[i]; if (vv !== null && vv !== undefined) { lastVal = vv; break; } }
      const item = document.createElement('div');
      item.className = chartId + '-legenditem';
      item.setAttribute('data-si', String(si));
      item.style.cssText = 'display:flex;align-items:center;gap:6px;font-size:11px;color:#475569;cursor:pointer;padding:4px 10px;border-radius:999px;background:#f8fafc;transition:background .15s,opacity .15s;user-select:none';
      item.innerHTML = '<span style="width:9px;height:9px;border-radius:50%;background:' + s.color + ';flex-shrink:0;display:inline-block"></span>'
        + '<span style="font-weight:700;color:#1e293b;white-space:nowrap">' + esc(s.name) + '</span>'
        + (lastVal !== null ? '<span style="font-weight:800;color:' + s.color + '">' + lastVal.toFixed(1) + '%</span>' : '');
      legend.appendChild(item);
    });
    wrap.appendChild(legend);
  }

  const tooltip = document.createElement('div');
  tooltip.style.cssText = 'position:absolute;pointer-events:none;background:#1e293b;color:#fff;border-radius:10px;padding:10px 12px;font-size:11px;box-shadow:0 8px 24px rgba(0,0,0,.28);opacity:0;transition:opacity .12s;z-index:20;min-width:140px;max-width:220px';
  wrap.appendChild(tooltip);

  const svgEl = wrap.querySelector('.' + chartId + '-svg') as SVGElement;
  const overlay = wrap.querySelector('.' + chartId + '-overlay') as SVGElement;
  const groupBgEls = wrap.querySelectorAll('.' + chartId + '-groupbg');
  const barEls = wrap.querySelectorAll('.' + chartId + '-bar');
  const labelEls = wrap.querySelectorAll('.' + chartId + '-label');
  const legendItems = wrap.querySelectorAll('.' + chartId + '-legenditem');
  const hiddenSeries: Record<string, boolean> = {};

  function applyVisibility(hoverSi: string | null) {
    barEls.forEach(function (r: any) {
      const si = r.getAttribute('data-si');
      const isHidden = hiddenSeries[si];
      const dim = hoverSi !== null && hoverSi !== undefined && String(hoverSi) !== si;
      r.style.opacity = isHidden ? '0' : (dim ? '.25' : '1');
    });
    labelEls.forEach(function (t: any) {
      const si = t.getAttribute('data-si');
      if (hiddenSeries[si]) t.style.opacity = '0';
    });
    legendItems.forEach(function (it: any) {
      const si = it.getAttribute('data-si');
      it.style.opacity = hiddenSeries[si] ? '.4' : '1';
      it.style.background = (hoverSi !== null && hoverSi !== undefined && String(hoverSi) === si) ? '#eef2f7' : '#f8fafc';
    });
  }
  legendItems.forEach(function (it: any) {
    it.addEventListener('mouseenter', function () { applyVisibility(it.getAttribute('data-si')); });
    it.addEventListener('mouseleave', function () { applyVisibility(null); });
    it.addEventListener('click', function () {
      const si = it.getAttribute('data-si');
      hiddenSeries[si] = !hiddenSeries[si];
      applyVisibility(null);
    });
  });

  function nearestGroup(clientX: number) {
    const rect = svgEl.getBoundingClientRect();
    const scaleX = rect.width ? W / rect.width : 1;
    const svgX = (clientX - rect.left) * scaleX;
    let idx = groupW > 0 ? Math.floor((svgX - padL) / groupW) : 0;
    if (idx < 0) idx = 0; if (idx > n - 1) idx = n - 1;
    return idx;
  }
  function showTooltip(idx: number, clientX: number, clientY: number) {
    groupBgEls.forEach(function (g: any) { g.setAttribute('opacity', g.getAttribute('data-i') === String(idx) ? '.05' : '0'); });
    const rows: { name: string; v: number; color: string }[] = [];
    trimSeries.forEach(function (s, si) {
      if (hiddenSeries[si]) return;
      const v = s.values[idx];
      if (v !== null && v !== undefined) rows.push({ name: s.name, v: v, color: colorFn ? colorFn(v) : s.color });
    });
    rows.sort(function (a, b) { return b.v - a.v; });
    let html = '<div style="font-weight:800;margin-bottom:6px;color:#cbd5e1;font-size:10px;text-transform:uppercase;letter-spacing:.5px">' + esc(trimLabels[idx]) + '</div>';
    rows.forEach(function (r) {
      html += '<div style="display:flex;align-items:center;gap:10px;justify-content:space-between;padding:2px 0"><span style="display:flex;align-items:center;gap:6px;min-width:0"><span style="width:8px;height:8px;border-radius:50%;background:' + r.color + ';display:inline-block;flex-shrink:0"></span><span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' + esc(r.name) + '</span></span><span style="font-weight:800;flex-shrink:0">' + r.v.toFixed(1) + '%</span></div>';
    });
    if (!rows.length) html += '<div style="color:#94a3b8">Sem dados</div>';
    tooltip.innerHTML = html;
    tooltip.style.opacity = '1';
    const wrapRect = wrap.getBoundingClientRect();
    let left = clientX - wrapRect.left + 14;
    const top = clientY - wrapRect.top - 10;
    if (left + 220 > wrapRect.width) left = clientX - wrapRect.left - 234;
    if (left < 0) left = 4;
    tooltip.style.left = left + 'px';
    tooltip.style.top = Math.max(0, top) + 'px';
  }
  function hideTooltip() {
    groupBgEls.forEach(function (g: any) { g.setAttribute('opacity', '0'); });
    tooltip.style.opacity = '0';
  }
  overlay.addEventListener('mousemove', function (e: any) { showTooltip(nearestGroup(e.clientX), e.clientX, e.clientY); });
  overlay.addEventListener('mouseleave', hideTooltip);
  overlay.addEventListener('touchstart', function (e: any) { if (e.touches && e.touches[0]) { const t = e.touches[0]; showTooltip(nearestGroup(t.clientX), t.clientX, t.clientY); } }, { passive: true });
  overlay.addEventListener('touchmove', function (e: any) { if (e.touches && e.touches[0]) { const t = e.touches[0]; showTooltip(nearestGroup(t.clientX), t.clientX, t.clientY); } }, { passive: true });
  overlay.addEventListener('touchend', hideTooltip);

  setTimeout(function () {
    requestAnimationFrame(function () {
      barEls.forEach(function (r: any) {
        r.style.transition = 'y .5s cubic-bezier(.22,1,.36,1),height .5s cubic-bezier(.22,1,.36,1)';
        r.setAttribute('y', r.getAttribute('data-final-y'));
        r.setAttribute('height', r.getAttribute('data-final-h'));
      });
      labelEls.forEach(function (t: any) { t.style.opacity = '1'; });
    });
  }, 0);

  return wrap;
}
